import { TimeService } from "@dexteel/mesf-core";
import moment from "moment-timezone";
import { useCallback, useEffect, useState } from "react";
import { MoveEventData, ProcessedRow } from "../models/utils";

interface useTableDragDropProps {
	filteredData: ProcessedRow[];
	onConfirmMove: (moveData: MoveEventData) => void;
}

export const useTableDragDrop = ({
	filteredData,
	onConfirmMove,
}: useTableDragDropProps) => {
	const [draggedEventId, setDraggedEventId] = useState<number | null>(null);
	const [draggedEventDates, setDraggedEventDates] = useState<{
		start: Date;
		end: Date;
	} | null>(null);
	const [dragOverEventId, setDragOverEventId] = useState<number | null>(null);
	const [dragPreviewDates, setDragPreviewDates] = useState<{
		start: Date;
		end: Date;
	} | null>(null);
	const [dragPreviewPosition, setDragPreviewPosition] = useState<{
		x: number;
		y: number;
	} | null>(null);
	const [dragPreviewAdjacentEvents, setDragPreviewAdjacentEvents] = useState<{
		eventBefore: ProcessedRow | null;
		eventAfter: ProcessedRow | null;
	} | null>(null);

	const cleanupDrag = useCallback(() => {
		setDraggedEventId(null);
		setDraggedEventDates(null);
		setDragOverEventId(null);
		setDragPreviewDates(null);
		setDragPreviewPosition(null);
		setDragPreviewAdjacentEvents(null);
	}, []);

	const handleDragStart = useCallback(
		(
			e: React.DragEvent,
			eventId: number,
			originalStart?: Date,
			originalEnd?: Date,
			statusCode?: string,
		) => {
			if (eventId < 0 || !originalStart || !originalEnd || statusCode !== "P") {
				e.preventDefault();
				return;
			}

			setDraggedEventId(eventId);
			setDraggedEventDates({ start: originalStart, end: originalEnd });
			e.dataTransfer.effectAllowed = "move";
			e.dataTransfer.setData("eventId", eventId.toString());
		},
		[],
	);

	const handleDragOver = useCallback(
		(
			e: React.DragEvent,
			targetEventId: number,
			targetOriginalEnd?: Date,
			targetStart?: Date,
		) => {
			e.preventDefault();

			// Don't allow dropping on summary rows or the same event
			if (
				targetEventId === -9999 || // MTD summary
				targetEventId === -99999 || // Month summary
				draggedEventId === targetEventId
			) {
				setDragOverEventId(null);
				setDragPreviewDates(null);
				return;
			}

			// For actual events, we need targetOriginalEnd
			// For empty days (negative EventId), we use targetStart
			if (targetEventId > 0 && !targetOriginalEnd) {
				setDragOverEventId(null);
				setDragPreviewDates(null);
				return;
			}

			if (targetEventId < 0 && !targetStart) {
				setDragOverEventId(null);
				setDragPreviewDates(null);
				return;
			}

			setDragOverEventId(targetEventId);

			if (draggedEventDates) {
				const serverTz = TimeService.getInstance().getServerTimeZone();
				const draggedStart = moment.tz(draggedEventDates.start, serverTz);
				const draggedEnd = moment.tz(draggedEventDates.end, serverTz);
				const duration = draggedEnd.diff(draggedStart);

				let newStart: moment.Moment;

				if (targetEventId < 0 && targetStart) {
					// Empty day: use target date + original event's time
					const targetDate = moment.tz(targetStart, serverTz);
					newStart = targetDate
						.clone()
						.hour(draggedStart.hour())
						.minute(draggedStart.minute())
						.second(draggedStart.second());
				} else {
					// Existing event: use the event's end as new start
					const overEnd = moment.tz(targetOriginalEnd, serverTz);
					newStart = overEnd.clone();
				}

				const newEnd = newStart.clone().add(duration);

				setDragPreviewDates({
					start: newStart.toDate(),
					end: newEnd.toDate(),
				});

				// Calculate adjacent events for preview
				const otherEvents = filteredData
					.filter(
						(row) =>
							row.IsFirstRow &&
							row.EventId !== draggedEventId &&
							row.EventId > 0 &&
							row.OriginalStart &&
							row.OriginalEnd,
					)
					.sort(
						(a, b) =>
							(a.OriginalStart?.getTime() ?? 0) -
							(b.OriginalStart?.getTime() ?? 0),
					);

				let eventBefore: ProcessedRow | null = null;
				let eventAfter: ProcessedRow | null = null;

				if (targetEventId > 0) {
					// Hovering over an existing event: that event is "before", find the next one as "after"
					const targetEvent = otherEvents.find(
						(row) => row.EventId === targetEventId,
					);
					if (targetEvent) {
						eventBefore = targetEvent;
						const targetIdx = otherEvents.indexOf(targetEvent);
						eventAfter = otherEvents[targetIdx + 1] ?? null;
					}
				} else {
					// Hovering over an empty day: find adjacent events around that day
					const targetTime = targetStart?.getTime() ?? 0;
					eventBefore =
						otherEvents
							.filter((row) => (row.OriginalEnd?.getTime() ?? 0) <= targetTime)
							.pop() ?? null;
					eventAfter =
						otherEvents.find(
							(row) => (row.OriginalStart?.getTime() ?? 0) >= targetTime,
						) ?? null;
				}

				setDragPreviewAdjacentEvents({ eventBefore, eventAfter });
			}

			setDragPreviewPosition({
				x: e.clientX + 15,
				y: e.clientY + 15,
			});
		},
		[draggedEventId, draggedEventDates, filteredData],
	);

	const handleDrop = useCallback(
		(
			e: React.DragEvent,
			targetEventId: number,
			targetOriginalEnd?: Date,
			targetStart?: Date,
		) => {
			e.preventDefault();

			if (!draggedEventId || !dragPreviewDates || !draggedEventDates) {
				cleanupDrag();
				return;
			}

			const draggedEvent = filteredData.find(
				(row) => row.EventId === draggedEventId && row.IsFirstRow,
			);

			if (!draggedEvent) {
				cleanupDrag();
				return;
			}

			// Find adjacent events
			const otherEvents = filteredData
				.filter(
					(row) =>
						row.IsFirstRow &&
						row.EventId !== draggedEventId &&
						row.EventId > 0 &&
						row.OriginalStart &&
						row.OriginalEnd,
				)
				.sort(
					(a, b) =>
						(a.OriginalStart?.getTime() ?? 0) -
						(b.OriginalStart?.getTime() ?? 0),
				);

			let eventBefore: ProcessedRow | null = null;
			let eventAfter: ProcessedRow | null = null;

			if (targetEventId > 0) {
				// Dropping after an existing event: that event is "before", find the next one as "after"
				const targetEvent = otherEvents.find(
					(row) => row.EventId === targetEventId,
				);
				if (targetEvent) {
					eventBefore = targetEvent;
					const targetIdx = otherEvents.indexOf(targetEvent);
					eventAfter = otherEvents[targetIdx + 1] ?? null;
				}
			} else {
				// Dropping on an empty day: find adjacent events around that day
				const targetTime = targetStart?.getTime() ?? 0;
				eventBefore =
					otherEvents
						.filter((row) => (row.OriginalEnd?.getTime() ?? 0) <= targetTime)
						.pop() ?? null;
				eventAfter =
					otherEvents.find(
						(row) => (row.OriginalStart?.getTime() ?? 0) >= targetTime,
					) ?? null;
			}

			onConfirmMove({
				draggedEvent,
				originalStart: draggedEventDates.start,
				originalEnd: draggedEventDates.end,
				newStart: dragPreviewDates.start,
				newEnd: dragPreviewDates.end,
				eventBefore,
				eventAfter,
			});
		},
		[
			draggedEventId,
			dragPreviewDates,
			draggedEventDates,
			filteredData,
			cleanupDrag,
			onConfirmMove,
		],
	);

	useEffect(() => {
		if (!draggedEventId) return;

		const handleKeyDown = (e: KeyboardEvent) => {
			if (e.key === "Escape") {
				cleanupDrag();
			}
		};

		window.addEventListener("keydown", handleKeyDown);
		return () => window.removeEventListener("keydown", handleKeyDown);
	}, [draggedEventId, cleanupDrag]);

	return {
		draggedEventId,
		dragOverEventId,
		dragPreviewDates,
		dragPreviewPosition,
		dragPreviewAdjacentEvents,
		handleDragStart,
		handleDragOver,
		handleDrop,
		cleanupDrag,
	};
};
